import { useIntl } from 'react-intl';
import {
  ListingProduct,
  DeliveryConditionsData,
  QualityMarkId,
} from 'types/ListingData';
import { Button } from 'ui/Button';
import { CustomDocument } from 'ui/Upload/Uploader/CustomDocument';
import { Divider } from 'ui/Divider';
import { StyledListingSummary } from './style';
import { DeliveryConditionsSummary } from './DeliveryConditionsSummary';
import { UploadFile } from 'antd';
import dayjs from 'dayjs';
import { routes } from 'config/config';
import { findQualityMark, isSameQualityMarkId } from 'utils/qualityMarkHelpers';

interface ExtendedUploadFile extends UploadFile {
  file_path?: string;
  file_name?: string;
  file_size?: number;
  file_format?: string;
  created_at?: string;
  fileId?: number;
}

interface QualityMark {
  id: QualityMarkId;
  name: string;
  logo?: string;
}

interface ListingSummaryProps {
  formData: Record<
    string,
    string | number | boolean | dayjs.Dayjs | number[] | undefined
  >;
  products: ListingProduct[];
  deliveryConditions: DeliveryConditionsData[] | null;
  contractFiles: ExtendedUploadFile[];
  qualityMarks: QualityMark[];
  onEditProduct: (productId: string) => void;
  onDeleteProduct: (productId: string) => void;
  onEditDelivery: (deliveryId: number) => void;
  onDeleteDelivery: (deliveryId: number) => void;
  onViewContract: (file: ExtendedUploadFile) => void;
}

export const ListingSummary = ({
  formData,
  products,
  deliveryConditions,
  contractFiles,
  qualityMarks,
  onEditProduct,
  onDeleteProduct,
  onEditDelivery,
  onDeleteDelivery,
  onViewContract,
}: ListingSummaryProps) => {
  const intl = useIntl();

  const formatValue = (
    value: string | number | boolean | dayjs.Dayjs | number[] | undefined
  ) => {
    if (value === undefined || value === null || Array.isArray(value)) {
      return '-';
    }
    if (dayjs.isDayjs(value)) {
      return value.format('DD.MM.YYYY H:mm');
    }
    return String(value);
  };

  const getTransactionTypeLabel = () => {
    const type = formData.transaction_type;
    const context = formData.transaction_context;
    const label = intl.formatMessage({
      id: `listings.transaction_type.${type}`,
    });

    return type === 'reservation' && context ? `${label} (${context})` : label;
  };

  const getQualityMarks = (): QualityMark[] => {
    const formQualityMarkIds = formData.quality_marks as
      | QualityMarkId[]
      | undefined;

    if (!formQualityMarkIds || formQualityMarkIds.length === 0) {
      const productQualityMarkIds: QualityMarkId[] = [];
      products.forEach((product) => {
        if (
          product.quality_mark_ids &&
          Array.isArray(product.quality_mark_ids)
        ) {
          product.quality_mark_ids.forEach((id) => {
            if (
              !productQualityMarkIds.some((existing) =>
                isSameQualityMarkId(existing, id)
              )
            ) {
              productQualityMarkIds.push(id);
            }
          });
        }
      });

      if (productQualityMarkIds.length === 0) {
        return [];
      }

      return productQualityMarkIds
        .map((id) => findQualityMark(qualityMarks, id))
        .filter((mark): mark is QualityMark => mark !== undefined);
    }

    return formQualityMarkIds
      .map((id) => findQualityMark(qualityMarks, id))
      .filter((mark): mark is QualityMark => mark !== undefined);
  };

  const getProductQualityMarks = (product: ListingProduct) => {
    if (!product.quality_mark_ids || !Array.isArray(product.quality_mark_ids)) {
      return [];
    }
    return qualityMarks.filter((qm) =>
      product.quality_mark_ids?.some((id) => isSameQualityMarkId(id, qm.id))
    );
  };

  const getListingApplicationTerm = () => {
    const startDate = formData.publication_start_date;
    const endDate = formData.publication_end_date;

    return `${formatValue(startDate)} - ${formatValue(endDate)}`;
  };

  const getAutoExtensionLabel = () => {
    const autoExtend = formData.auto_extend_publication;
    const extendDays = formData.extend_days;

    if (autoExtend && extendDays) {
      return `${extendDays} ${intl.formatMessage({ id: 'general.days' })}`;
    }

    return intl.formatMessage({ id: 'general.no' });
  };

  const getDeliveryTypeLabel = () => {
    const deliveryType = formData.delivery_type;
    if (!deliveryType) {
      return '-';
    }
    return intl.formatMessage({ id: `listings.delivery_type.${deliveryType}` });
  };

  return (
    <StyledListingSummary>
      <div className="summary-section">
        <h3 className="summary-section-title">
          {intl.formatMessage({ id: 'navigation.base_information' })}
        </h3>
        <div className="summary-section-content">
          <div className="summary-info-row">
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.identifier.long' })}
              </span>
              <span className="summary-info-value">
                {formatValue(formData.identifier)}
              </span>
            </div>
          </div>

          <Divider marginTop={4} marginBottom={4} />

          <div className="summary-info-row">
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'general.title' })}
              </span>
              <span className="summary-info-value">
                {formatValue(formData.title)}
              </span>
            </div>
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.transaction_type' })}
              </span>
              <span className="summary-info-value">
                {getTransactionTypeLabel()}
              </span>
            </div>
          </div>

          <Divider marginTop={4} marginBottom={4} />

          <div className="summary-info-row">
            <div className="summary-info-column summary-info-full-width">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.listing_description' })}
              </span>
              <span className="summary-info-value">
                {formatValue(formData.description)}
              </span>
            </div>
          </div>

          <Divider marginTop={4} marginBottom={4} />

          <div className="summary-info-row">
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.private_listing' })}
              </span>
              <span className="summary-info-value">
                {formData.is_private
                  ? formatValue(formData.private_link_full)
                  : intl.formatMessage({ id: 'listings.not_private' })}
              </span>
            </div>
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.single_producer_only' })}
              </span>
              <span className="summary-info-value">
                {formData.single_producer_only
                  ? intl.formatMessage({ id: 'general.yes' })
                  : intl.formatMessage({ id: 'general.no' })}
              </span>
            </div>
          </div>

          <Divider marginTop={4} marginBottom={4} />

          <div className="summary-info-row">
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.publication_dates' })}
              </span>
              <span className="summary-info-value">
                {getListingApplicationTerm()}
              </span>
            </div>
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.auto_extend_publication' })}
              </span>
              <span className="summary-info-value">
                {getAutoExtensionLabel()}
              </span>
            </div>
          </div>

          <Divider marginTop={4} marginBottom={4} />

          <div className="summary-info-row">
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listings.delivery_radius' })}
              </span>
              <span className="summary-info-value">
                {formatValue(formData.delivery_radius_km)} km
              </span>
            </div>
            <div className="summary-info-column">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'listing.summary.quality_marks' })}
              </span>
              <span className="summary-info-value">
                {getQualityMarks().length === 0 ? (
                  '-'
                ) : (
                  <div className="quality-marks-list">
                    {getQualityMarks().map((mark: QualityMark) => (
                      <span key={mark.id} className="quality-mark-item">
                        {mark.name}
                      </span>
                    ))}
                  </div>
                )}
              </span>
            </div>
          </div>

          <Divider marginTop={4} marginBottom={4} />

          <div className="summary-info-row">
            <div className="summary-info-column summary-info-full-width">
              <span className="summary-info-label">
                {intl.formatMessage({ id: 'general.address' })}
              </span>
              <span className="summary-info-value">
                {formatValue(formData.address)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <Divider marginBottom={4} />
        <h3 className="summary-section-title">
          {intl.formatMessage({ id: 'listings.form.products' })}
        </h3>
        <div className="summary-section-content">
          {products.map((product, index) => (
            <div key={product.id} className="summary-product-item">
              <div className="product-tag">{index + 1}</div>
              <div className="product-info">
                <span className="product-name">
                  {product.name || product.category?.name}
                </span>
                <span className="product-details">
                  ({intl.formatMessage({ id: 'listings.form.quantity' })}:{' '}
                  {product.required_quantity} {product.storage_unit?.short}
                  {product.min_acceptable_offer_quantity &&
                    product.min_acceptable_offer_quantity > 0 && (
                      <>
                        ,{' '}
                        {intl.formatMessage({
                          id: 'listings.min_acceptable_offer_quantity',
                        })}
                        : {product.min_acceptable_offer_quantity}{' '}
                        {product.storage_unit?.short}
                      </>
                    )}
                  )
                </span>
                <div className="product-quality-mark-logos">
                  {getProductQualityMarks(product).map(
                    (qmark) =>
                      qmark.logo && (
                        <img
                          key={qmark.id}
                          src={`${routes.api.baseUrl}${qmark.logo}`}
                          alt={qmark.name || ''}
                          title={qmark.name}
                        />
                      )
                  )}
                </div>
              </div>
              <div className="product-actions">
                <div className="action-buttons">
                  <Button
                    label={intl.formatMessage({ id: 'general.edit' })}
                    onClick={() => onEditProduct(product.id!)}
                    className="action-button"
                  />
                  <Button
                    label={intl.formatMessage({ id: 'general.delete' })}
                    onClick={() => onDeleteProduct(product.id!)}
                    className="action-button"
                    disabled={products.length === 1}
                  />
                </div>
                {products.length === 1 && (
                  <span className="product-delete-disabled-text">
                    {intl.formatMessage({
                      id: 'listings.product_delete_disabled',
                    })}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="summary-section">
        <Divider marginBottom={4} />
        <h3 className="summary-section-title">
          {intl.formatMessage({ id: 'listings.form.delivery_conditions' })}
        </h3>
        <div className="summary-section-content">
          {deliveryConditions && deliveryConditions.length > 0 ? (
            <DeliveryConditionsSummary
              deliveryConditions={deliveryConditions}
              products={products}
              onEditDelivery={onEditDelivery}
              onDeleteDelivery={onDeleteDelivery}
            />
          ) : (
            <div className="summary-field-value">{getDeliveryTypeLabel()}</div>
          )}
        </div>
      </div>

      <div className="summary-section">
        <Divider marginBottom={4} />
        <h3 className="summary-section-title">
          {intl.formatMessage({ id: 'listings.form.contract_period' })}
        </h3>
        <div className="summary-section-content">
          <div className="summary-field">
            <span className="summary-field-value">
              {formatValue(formData.contract_term_start_date)} -{' '}
              {formatValue(formData.contract_term_end_date)}
            </span>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <Divider />
        <h3 className="summary-section-title">
          {intl.formatMessage({ id: 'listings.form.contract_sample' })}
        </h3>
        <div className="summary-section-content">
          {contractFiles && contractFiles.length > 0 ? (
            <div className="contract-files-list">
              {contractFiles.map((file, index) => (
                <CustomDocument
                  key={file.uid || index}
                  file={file}
                  onDownload={() => onViewContract(file)}
                />
              ))}
            </div>
          ) : (
            <div className="summary-field-value">
              {intl.formatMessage({ id: 'listings.form.no_contract_file' })}
            </div>
          )}
        </div>
      </div>
    </StyledListingSummary>
  );
};
